import { GuestRelation } from '../types/Guests';
import { guestRoleLabel } from '../utils/guestRoles';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';
import { ProjectScopedCache } from './projectScopedCache';

// Guest-to-guest directional relationships with labels, backed by
// kalligo-api's guest_relations table.

export const GUEST_RELATIONS_CHANGE_EVENT = 'kalligo_guest_relations_change';

// Cache of relations for a single guest, keyed by guest ID
const relationsCache = new ProjectScopedCache<Map<string, GuestRelation[]>>(() => new Map());

export const loadGuestRelations = (guestId: string): GuestRelation[] => {
  return relationsCache.load().get(guestId) ?? [];
};

function saveRelationsCache(guestId: string, relations: GuestRelation[]): void {
  const next = new Map(relationsCache.load());
  next.set(guestId, relations);
  relationsCache.save(next);
  window.dispatchEvent(new Event(GUEST_RELATIONS_CHANGE_EVENT));
}

function clearRelationsCache(guestId: string): void {
  const next = new Map(relationsCache.load());
  next.delete(guestId);
  relationsCache.save(next);
  window.dispatchEvent(new Event(GUEST_RELATIONS_CHANGE_EVENT));
}

// The API names guests by GS-### ref wherever it can; the uuid is only the fallback.
function mapApiRow(row: any): GuestRelation {
  return {
    id: row.id,
    guestId: row.guest_id,
    relatedGuestId: row.related_guest_ref ?? row.related_guest_id,
    relationshipLabel: row.relationship_label,
    relatedGuestName: row.related_guest_name,
    relatedGuestRole: row.related_guest_role ?? null,
    relatedGuestIsHonoree: row.related_guest_is_honoree === true,
    createdAt: row.created_at,
  };
}

export const refreshGuestRelations = async (guestId: string): Promise<GuestRelation[]> => {
  const key = relationsCache.currentKey();
  try {
    const res = await apiGet<any>(planningPath(`/guests/${guestId}/relations`));
    // a refresh that finishes after a project switch is dropped, never saved into the project now open
    if (Array.isArray(res?.relations) && relationsCache.currentKey() === key) {
      saveRelationsCache(guestId, res.relations.map(mapApiRow));
    }
  } catch {
    // API unreachable — keep existing cache
  }
  return loadGuestRelations(guestId);
};

// A self-relation (guestId === relatedGuestId) is rejected both here and by
// the server's `guest_relations_not_self` CHECK constraint — this client-side
// guard exists so the picker fails fast with a clear message instead of a
// round trip to the API just to hit the DB constraint.
export class SelfRelationError extends Error {
  constructor() {
    super('A guest cannot have a relation to themselves.');
    this.name = 'SelfRelationError';
  }
}

export const addGuestRelation = async (
  guestId: string,
  relatedGuestId: string,
  relationshipLabel: string,
): Promise<GuestRelation> => {
  if (guestId === relatedGuestId) {
    throw new SelfRelationError();
  }
  // The body repeats the guest in the URL (the API checks they are the same guest).
  const created = await apiPost<any>(planningPath(`/guests/${guestId}/relations`), {
    guest_id: guestId,
    related_guest_id: relatedGuestId,
    relationship_label: relationshipLabel,
  });
  await refreshGuestRelations(guestId);
  return mapApiRow(created);
};

export const updateGuestRelation = async (
  guestId: string,
  relationId: string,
  relationshipLabel: string,
): Promise<GuestRelation | null> => {
  const updated = await apiPut<any>(planningPath(`/guests/${guestId}/relations/${relationId}`), {
    relationship_label: relationshipLabel,
  });
  await refreshGuestRelations(guestId);
  return updated ? mapApiRow(updated) : null;
};

export const removeGuestRelation = async (guestId: string, relationId: string): Promise<void> => {
  await apiDelete(planningPath(`/guests/${guestId}/relations/${relationId}`));
  await refreshGuestRelations(guestId);
};

// Derives the display text for a relation row — "Father of Jane Doe" normally,
// or "Father of Jane Doe (Bride)" when the related guest is an honoree with a role.
// Computed here at render time from the joined related_guest_role and
// related_guest_is_honoree,
// never stored — same "never a second stored copy" principle applies.
export function describeGuestRelation(
  r: Pick<GuestRelation, 'relationshipLabel' | 'relatedGuestName' | 'relatedGuestRole' | 'relatedGuestIsHonoree'>,
): string {
  const base = `${r.relationshipLabel} ${r.relatedGuestName}`;
  if (!r.relatedGuestIsHonoree || !r.relatedGuestRole) return base;
  return `${base} (${guestRoleLabel(r.relatedGuestRole)})`;
}
